import React from 'react';
import { isPast } from 'date-fns';
import type { Variants } from 'framer-motion';
import { motion } from 'framer-motion';
import Router from 'next/router';
import { gql } from '@soundxyz/gql-string';
import { styled } from '@components-shared/src/stitches';
import { GetNotifiedButton } from '@components/EmailNotifications/GetNotifiedButton';
import { Background } from '@components/Song/Hero/Background';
import { Countdown } from '@components/Song/Hero/Countdown';
import { SongCardDropdown } from '@components/SongCards/shared/SongCardDropdown';
import { AccessibleLink } from '@components/main-components/link/AccessibleLink';
import { PlayButton } from '@components/main-components/play-button/PlayButton';
import { MediaQuery } from '@context/mediaQuery';
import type { FragmentType } from '@graphql/reactQuery';
import { getFragment } from '@graphql/reactQuery';
import { FeedReleaseCardFragmentDoc } from '@graphql/reactQuery';
import { usePlayButton } from '@hooks/usePlayButton';
import { useAudioController } from '@store/audio/AudioController';
import { webappUriPath } from '@utils/url';
import { CARD_CONTAINER_CSS } from '../shared';
import { CoverImage } from './CoverImage';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment FeedReleaseCard on Release {
    id
    title
    mintStartTimestamp
    webappUri
    type
    artist {
      id
      name
      webappUri
      userId
    }
    track {
      id
    }
    coverImage {
      id
      url
    }
    staticCoverImage {
      id
      url
    }
    animatedCoverImage {
      id
      url
    }
    ...SongCardDropdownInfo
  }
`);

export const FeedReleaseCard = ({
  releaseFrag,
}: {
  releaseFrag: FragmentType<FeedReleaseCardFragmentDoc>;
}) => {
  const { tablet } = MediaQuery.useContainer();
  const [, forceUpdate] = React.useState(0);
  const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);
  const release = getFragment(FeedReleaseCardFragmentDoc, releaseFrag);
  const {
    coverImage,
    animatedCoverImage,
    staticCoverImage,
    track,
    title,
    artist,
    mintStartTimestamp,
  } = release;

  const hideCountdown = isPast(mintStartTimestamp);

  const isAudioEnabled = release.type !== 'ALBUM';

  const variants: Variants = {
    initial: {
      opacity: tablet ? 1 : 0,
      y: 0,
    },

    animate: {
      opacity: 1,
      y: tablet ? (hideCountdown ? 60 : 0) : 0,
    },
  };

  const releaseLink = webappUriPath(release.webappUri);

  const { playing, activeTrackId } = useAudioController();

  const isPlaying = track.id === activeTrackId && playing;
  const {
    onPlayClick,
    isLoading,
    ref: playButtonPrefetchRef,
  } = usePlayButton({
    isAlbum: release.type === 'ALBUM',
    trackId: release.track.id,
  });

  React.useEffect(() => {
    //prefetch release page
    Router.prefetch(releaseLink);
  }, [releaseLink]);

  return (
    <CardContainer
      onClick={() => {
        Router.push(releaseLink);
      }}
    >
      {tablet && <Background coverImage={staticCoverImage?.url || coverImage.url} rounded />}
      {coverImage.url && (
        <CoverWrapper>
          <div className="three-dots-menu">
            <SongCardDropdown
              triggerStyles={{
                transition: 'background 0.2s',
                '&:hover': { background: '$grey600' },
                svg: {
                  path: {
                    fill: '$white',
                  },
                },
              }}
              isDropdownOpen={isDropdownOpen}
              setIsDropdownOpen={setIsDropdownOpen}
              release={release}
            />
          </div>
          <CoverImage
            releaseTitle={title}
            isAnimated={!!animatedCoverImage}
            coverImage={animatedCoverImage?.url || coverImage.url}
            trackDisabled={!isAudioEnabled}
            hasCountdown={!hideCountdown}
            webappUri={release.webappUri}
          />
        </CoverWrapper>
      )}
      <ReleaseInfoContainer
        hasCountdown={!hideCountdown}
        variants={variants}
        initial="initial"
        animate="animate"
        transition={{
          ease: 'easeInOut',
        }}
      >
        <Wrapper hideCountdown={hideCountdown}>
          {hideCountdown && (
            <PlayButton
              color="white"
              playing={isPlaying}
              onClick={e => {
                e.stopPropagation();
                onPlayClick();
              }}
              loading={isLoading}
              disabled={!isAudioEnabled}
              size="l"
              ref={playButtonPrefetchRef}
            />
          )}

          <ReleaseInfo hideCountdown={hideCountdown}>
            <ArtistAccessibleLink
              pathnameUri={webappUriPath(artist.webappUri)}
              onClick={e => {
                e.stopPropagation();
              }}
            >
              <h3 className="artistName">{artist.name}</h3>
            </ArtistAccessibleLink>
            {!!title && (
              <StyledAccessibleLink
                pathnameUri={webappUriPath(release.webappUri)}
                hideCountdown={hideCountdown}
              >
                <Title
                  css={{
                    justifyContent: hideCountdown ? 'flex-start' : 'center',
                    textAlign: hideCountdown ? 'left' : 'center',

                    '@tablet': {
                      justifyContent: 'flex-start',
                      textAlign: 'left',
                    },
                  }}
                >
                  {title}
                </Title>
              </StyledAccessibleLink>
            )}
          </ReleaseInfo>
        </Wrapper>
        {!hideCountdown && (
          <Wrapper>
            <TimerContainer>
              <Countdown
                mintStartTimestamp={mintStartTimestamp}
                css={{
                  gap: 12,
                  '@tablet': {
                    alignItems: 'flex-start',
                  },
                }}
                onCountdownExpire={() => forceUpdate(v => ++v)}
              />
            </TimerContainer>
            {!hideCountdown && (
              <GetNotifiedButton
                type="feed_release_card"
                artistId={artist.id}
                userId={artist.userId}
              />
            )}
          </Wrapper>
        )}
      </ReleaseInfoContainer>
    </CardContainer>
  );
};

const ArtistAccessibleLink = styled(AccessibleLink, {
  maxWidth: '100%',
});

const StyledAccessibleLink = styled(AccessibleLink, {
  maxWidth: '100%',
  paddingX: 8,

  '@tablet': {
    paddingX: 0,
  },

  variants: {
    hideCountdown: {
      true: {
        paddingX: 0,
      },
    },
  },
});

const Title = styled('h3', {
  title: 'L',
  fontSize: 24,
  fontWeight: 500,
  color: '$white',
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  whiteSpace: 'pre-line',
  display: '-webkit-box',
  '-webkit-line-clamp': 5,
  '-webkit-box-orient': 'vertical',

  '@tablet': {
    textAlign: 'left',
  },
});

const CardContainer = styled('div', {
  outline: 'none',
  border: 'none',
  display: 'flex',
  flexDirection: 'row-reverse',
  minWidth: 0,
  aspectRatio: '1 / 1',
  ...CARD_CONTAINER_CSS,

  '@tablet': {
    aspectRatio: 'unset',
  },
});

const ReleaseInfoContainer = styled(motion.div, {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  gap: 32,
  alignItems: 'center',
  height: '100%',
  width: '100%',
  overflow: 'hidden',
  position: 'absolute',

  '@tablet': {
    position: 'relative',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    padding: 30,
    width: '100%',
  },

  variants: {
    hasCountdown: {
      false: {
        justifyContent: 'flex-end',
        paddingBottom: 20,

        '@tablet': {
          paddingBottom: 30,
          justifyContent: 'flex-start',
        },
      },
    },
  },
});

const ReleaseInfo = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  width: '100%',
  gap: 4,
  overflow: 'hidden',

  '.artistName': {
    base: 'S',
    lineHeight: '14px',
    color: '#fff',
    fontWeight: 600,
  },

  '@tablet': {
    marginTop: 2,
    alignItems: 'flex-start',
  },

  variants: {
    hideCountdown: {
      true: {
        paddingX: 10,
        alignItems: 'flex-start',
      },
    },
  },
});

const Wrapper = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  scrollBehavior: 'smooth',

  '@tablet': {
    alignItems: 'flex-start',
  },

  variants: {
    hideCountdown: {
      true: {
        alignItems: 'center',
        flexDirection: 'column',
        height: '58%',
        width: '100%',
        justifyContent: 'space-between',
        minWidth: 0,

        '@tablet': {
          flexDirection: 'row',
          gap: 12,
        },
      },
    },
  },
});

const TimerContainer = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',

  '@tablet': {
    alignItems: 'flex-start',
  },
});

const CoverWrapper = styled('div', {
  display: 'flex',
  flexShrink: 0,
  overflow: 'hidden',
  height: '100%',
  width: '100%',
  position: 'relative',
  '.three-dots-menu': {
    position: 'absolute',
    right: 12,
    top: 12,
    '@tablet': {
      right: 20 + 4,
      top: 20 + 4,
    },
    zIndex: '$above1',
  },
  '@tablet': {
    padding: 20,
    aspectRatio: '1 / 1',
    width: 'unset',
    justifyContent: 'flex-end',
  },
});
